import { Component, ElementRef, HostListener, Input, Output, EventEmitter, TemplateRef, signal, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './dropdown.html',
  styleUrl: './dropdown.scss',
})
export class Dropdown {
  private readonly el = inject(ElementRef);

  @Input() label = '';
  @Input() placeholder = 'Select…';
  @Input() triggerValue: string | null = null;
  @Input() isLoading = false;
  @Input() loadError = false;
  @Input() loadErrorMessage = 'Failed to load options';
  @Input() emptyMessage = 'No options available';
  @Input() disabled = false;
  @Input() isSaving = false;
  @Input() saveError = false;
  @Input() saveErrorMessage = 'Failed to switch. Please try again.';
  @Input() items: unknown[] = [];
  /** Return a stable string ID for each item — used to mark the selected option. */
  @Input() itemId: ((item: unknown) => string) | null = null;
  @Input() selectedId: string | null = null;
  /** Optional template for each item. Context: `{ $implicit: item }`. Falls back to `{{ item }}`. */
  @Input() itemTemplate: TemplateRef<{ $implicit: unknown }> | null = null;

  @Output() opened = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<unknown>();

  isOpen = signal(false);

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (this.isOpen() && !this.el.nativeElement.contains(target)) {
      this.isOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
    }
  }

  toggle(): void {
    if (this.disabled) return;
    if (this.isOpen()) {
      this.isOpen.set(false);
    } else {
      this.isOpen.set(true);
      this.opened.emit();
    }
  }

  close(): void {
    this.isOpen.set(false);
  }

  select(item: unknown): void {
    this.itemSelected.emit(item);
    // Don't auto-close — host decides after its async work completes
  }

  isSelected(item: unknown): boolean {
    if (this.selectedId == null || this.itemId == null) return false;
    return this.itemId(item) === this.selectedId;
  }
}
